import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Check, X, AlertCircle, Trash2 } from 'lucide-react';
import { apiService } from '../../services/api';

interface Consulta {
  _id: string;
  paciente?: {
    nome: string;
    email: string;
  };
  pacienteNome?: string;
  dataHora: string;
  status: 'pendente' | 'aprovada' | 'rejeitada' | 'agendada';
  modalidade?: string;
  createdAt: string;
}

const Consultas: React.FC = () => {
  const [consultas, setConsultas] = useState<Consulta[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [processando, setProcessando] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    carregarConsultas();
  }, []);

  const carregarConsultas = async () => {
    try {
      const data = await apiService.getConsultas();
      // Ordenar por data (mais recentes primeiro) e garantir modalidade padrão
      const consultasOrdenadas = data.map((consulta: Consulta) => ({
        ...consulta,
        modalidade: consulta.modalidade ?? 'Presencial'
      })).sort((a: Consulta, b: Consulta) => 
        new Date(b.dataHora).getTime() - new Date(a.dataHora).getTime()
      );
      setConsultas(consultasOrdenadas);
    } catch (error) {
      console.error('Erro ao carregar consultas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAprovar = async (id: string) => {
    try {
      setProcessando(id);
      await apiService.approveConsulta(id);
      await carregarConsultas(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao aprovar consulta:', error);
    } finally {
      setProcessando(null);
    }
  };

  const handleRejeitar = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja rejeitar esta consulta?')) {
      return;
    }

    try {
      setProcessando(id);
      await apiService.deleteConsulta(id);
      await carregarConsultas(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao rejeitar consulta:', error);
    } finally {
      setProcessando(null);
    }
  };

  const handleRemover = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja remover esta consulta?')) {
      return;
    }

    try {
      setProcessando(id);
      await apiService.deleteConsulta(id);
      await carregarConsultas(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao remover consulta:', error);
    } finally {
      setProcessando(null);
    }
  };

  const formatarData = (dataString: string) => {
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR');
  };

  const formatarHora = (dataString: string) => {
    const data = new Date(dataString);
    return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const getNomePaciente = (consulta: Consulta) => {
    const nome = consulta.paciente?.nome ?? consulta.pacienteNome ?? 'Paciente não identificado';
    return nome.toUpperCase();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'aprovada':
        return <span className="badge bg-success">Aprovada</span>;
      case 'pendente':
        return <span className="badge bg-warning">Pendente</span>;
      case 'agendada':
        return <span className="badge bg-info">Agendada</span>;
      case 'rejeitada':
        return <span className="badge bg-danger">Rejeitada</span>;
      default:
        return <span className="badge bg-secondary">Pendente</span>;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <output className="spinner-border text-success">
          <span className="visually-hidden">Carregando...</span>
        </output>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="text-success mb-0">Consultas</h2>
      </div>

      <div className="card shadow-sm">
        <div className="card-body p-0">
          {isMobile ? (
            // Mobile: Cards
            <div className="p-2">
              {consultas.map((consulta) => (
                <div key={consulta._id} className="card mb-1 border">
                  <div className="card-body" style={{padding: '0.25rem'}}>
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <h6 className="card-title mb-1 fw-bold">{getNomePaciente(consulta)}</h6>
                      {getStatusBadge(consulta.status)}
                    </div>
                    <div className="text-muted small" style={{marginBottom: '0.125rem'}}>
                      <Calendar size={12} className="me-1" />
                      {formatarData(consulta.dataHora)}
                    </div>
                    <div className="text-muted small" style={{marginBottom: '0.125rem'}}>
                      <Clock size={12} className="me-1" />
                      {formatarHora(consulta.dataHora)}
                    </div>
                    <div className="text-muted small" style={{marginBottom: '0.25rem'}}>
                      <User size={12} className="me-1" />
                      {consulta.modalidade ?? 'Presencial'}
                    </div>
                    
                    {(consulta.status === 'pendente' || consulta.status === 'agendada' || !consulta.status) && (
                      <div className="d-flex gap-1 w-100">
                        <button 
                          className="btn btn-success btn-sm" 
                          onClick={() => handleAprovar(consulta._id)}
                          disabled={processando === consulta._id}
                          style={{minHeight: '44px', flex: '1'}}
                        >
                          {processando === consulta._id ? (
                            <output className="spinner-border spinner-border-sm">
                              <span className="visually-hidden">Processando...</span>
                            </output>
                          ) : (
                            <>
                              <Check size={16} className="me-1" />
                              Aprovar
                            </>
                          )}
                        </button>
                        <button 
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRejeitar(consulta._id)}
                          disabled={processando === consulta._id}
                          style={{minHeight: '44px', flex: '1'}}
                        >
                          <X size={16} className="me-1" />
                          Rejeitar
                        </button>
                      </div>
                    )}
                    
                    <div className="d-flex gap-1 w-100 mt-1">
                      <button 
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleRemover(consulta._id)}
                        disabled={processando === consulta._id}
                        style={{minHeight: '44px', width: '100%', border: '1px solid #dc3545'}}
                      >
                        <Trash2 size={16} className="me-1" />
                        Remover
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Desktop: Tabela
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Paciente</th>
                    <th>Data</th>
                    <th>Horário</th>
                    <th>Modalidade</th>
                    <th>Status</th>
                    <th style={{width: '150px', textAlign: 'center'}}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {consultas.map((consulta) => (
                    <tr key={consulta._id}>
                      <td className="fw-semibold">
                        <User size={16} className="me-2 text-muted" />
                        {getNomePaciente(consulta)}
                      </td>
                      <td>{formatarData(consulta.dataHora)}</td>
                      <td>{formatarHora(consulta.dataHora)}</td>
                      <td>{consulta.modalidade ?? 'Presencial'}</td>
                      <td>{getStatusBadge(consulta.status)}</td>
                      <td>
                        {(consulta.status === 'pendente' || consulta.status === 'agendada' || !consulta.status) ? (
                          <div className="d-flex gap-1 justify-content-center">
                            <button 
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleAprovar(consulta._id)}
                              disabled={processando === consulta._id}
                              title="Aprovar"
                            >
                              {processando === consulta._id ? (
                                <output className="spinner-border spinner-border-sm">
                                  <span className="visually-hidden">Processando...</span>
                                </output>
                              ) : (
                                <Check size={16} />
                              )}
                            </button>
                            <button 
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRejeitar(consulta._id)}
                              disabled={processando === consulta._id}
                              title="Rejeitar"
                            >
                              <X size={16} />
                            </button>
                            <button 
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemover(consulta._id)}
                              disabled={processando === consulta._id}
                              title="Remover"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex gap-1 justify-content-center">
                            <button 
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemover(consulta._id)}
                              disabled={processando === consulta._id}
                              title="Remover"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {consultas.length === 0 && (
        <div className="text-center py-5">
          <div className="text-muted mb-3">
            <AlertCircle size={48} />
          </div>
          <h5 className="text-muted">Nenhuma consulta encontrada</h5>
          <p className="text-muted">As consultas agendadas aparecerão aqui</p>
        </div>
      )}
    </div>
  );
};

export default Consultas;